import { useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { fmtLocalDate } from '../../../utils/datetime';
import { useDeptEmployees, useAvailableEmployees, useLinkEmployee, useUnlinkEmployee, useDeleteEmployee, errMessage, type DeptEmployee } from './systemMasters';
import { EmployeeFormDialog } from './EmployeeFormDialog';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';

const GENDER_LABEL = (g: number | null) => (g === 1 ? 'Male' : g === 2 ? 'Female' : g === 0 ? 'None' : '');
// Sentinel passed to the "available employees" endpoint when the department is
// new (unsaved): WHERE (Department IS NULL OR Department <> @Dept) then returns
// every linkable employee, so Link works before the department has an Oid.
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * The Employees child grid inside the Department detail — mirrors the legacy
 * `Department_Employees_ListView` (Department → Employee, Employee.Department FK,
 * a non-aggregated reference). LIMS allows Link + Unlink (no New from here);
 * Link sets Employee.Department, Unlink clears it (the Employee is kept).
 * For a NEW (unsaved) department LIMS still allows Link — the associations are
 * staged in memory and committed when the department is saved. We mirror that:
 * `staged`/`onStagedChange` hold the pending employee Oids until the parent save
 * commits them to the freshly-created department.
 */
export function DepartmentEmployeesPanel({ departmentId, staged, onStagedChange }: {
  departmentId?: string; staged?: Set<string>; onStagedChange?: (s: Set<string>) => void;
}) {
  const staging = !departmentId;
  const notify = useNotify();
  const { confirm } = useConfirm();
  const { data: deptRows = [], isFetching } = useDeptEmployees(departmentId);
  const { data: available = [] } = useAvailableEmployees(departmentId ?? EMPTY_GUID);
  const link = useLinkEmployee(departmentId ?? '');
  const unlink = useUnlinkEmployee(departmentId ?? '');
  const del = useDeleteEmployee();

  const stagedIds = staged ?? new Set<string>();
  const rows = staging ? available.filter((e) => stagedIds.has(e.id)) : deptRows;

  const [editId, setEditId] = useState<string | null>(null);   // Employee being edited (full form)
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkSel, setLinkSel] = useState<Set<string | number>>(new Set());
  const [linkSearch, setLinkSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const openLink = () => { setLinkSel(new Set()); setLinkSearch(''); setLinkOpen(true); };
  const linkRows = available.filter((e) => !stagedIds.has(e.id)).filter((e) => {
    const q = linkSearch.trim().toLowerCase();
    return !q || [e.fullName, e.displayName, e.jobTitle, e.email].some((v) => (v ?? '').toLowerCase().includes(q));
  });

  const doLink = async () => {
    if (linkSel.size === 0) { notify.warning('Select at least one employee to link.'); return; }
    if (staging) {   // new department — stage the links, commit on parent save
      const next = new Set(stagedIds); linkSel.forEach((id) => next.add(String(id)));
      onStagedChange?.(next); setLinkOpen(false); setLinkSel(new Set()); return;
    }
    setBusy(true);
    try {
      for (const id of linkSel) await link.mutateAsync(String(id));
      setLinkOpen(false); setLinkSel(new Set());
    } catch (e) { notify.error(errMessage(e)); }
    finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one employee to unlink.'); return; }
    const ok = await confirm({
      title: 'Unlink', tone: 'danger', confirmLabel: 'Unlink',
      message: `Remove ${selectedIds.size} employee${selectedIds.size === 1 ? '' : 's'} from this department? (The employee itself is kept.)`,
    });
    if (!ok) return;
    if (staging) {   // new department — drop from the staged set
      const next = new Set(stagedIds); selectedIds.forEach((id) => next.delete(String(id)));
      onStagedChange?.(next); setSelectedIds(new Set()); return;
    }
    try { for (const id of selectedIds) await unlink.mutateAsync(String(id)); setSelectedIds(new Set()); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const editSelected = () => {
    if (selectedIds.size !== 1) { notify.warning('Select exactly one employee to edit.'); return; }
    setEditId(String([...selectedIds][0]));
  };
  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one employee to delete.'); return; }
    const ok = await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} employee${selectedIds.size === 1 ? '' : 's'}?` });
    if (!ok) return;
    try { for (const id of selectedIds) await del.mutateAsync(String(id)); setSelectedIds(new Set()); }
    catch (e) { notify.error(errMessage(e)); }
  };

  const cmp = (get: (r: DeptEmployee) => string | null) => (a: DeptEmployee, b: DeptEmployee) =>
    (get(a) ?? '').localeCompare(get(b) ?? '', undefined, { sensitivity: 'base' });
  // Read-only checkbox cell (LIMS renders bit columns as a disabled checkbox, not Yes/No text).
  const check = (v: boolean) => (
    <input type="checkbox" className="acc-grid-check" checked={v} onChange={() => {}} onClick={(e) => e.preventDefault()} />
  );

  const cols: AppColumn<DeptEmployee>[] = [
    {
      field: 'edit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) => <button className="pts-icon-btn edit" title="Edit" onClick={(e) => { e.stopPropagation(); setEditId(r.id); }}><i className="ti ti-edit" /></button>,
    },
    // Full 1:1 LIMS Department_Employees_ListView column set, in LIMS order.
    // Password shows a BLANK column (the real value is never read — security).
    // showAll = IsLIMSAdministrator; Start Date = Activity.StartDateOn; Role Name duplicates Role.
    // Captions match the LIMS XAF property-name headers exactly.
    { field: 'displayName', header: 'DisplayName', width: 90, text: (r) => r.displayName ?? '', compare: cmp((r) => r.displayName) },
    { field: 'gender', header: 'Gender', width: 65, sortable: false, text: (r) => GENDER_LABEL(r.gender) },
    { field: 'showAll', header: 'ShowAll', width: 65, align: 'center', sortable: false, filterable: false, render: (r) => check(r.isLIMSAdministrator) },
    { field: 'fullName', header: 'FullName', width: 95, sortable: false, text: (r) => r.fullName ?? '' },
    { field: 'state', header: 'State', width: 65, sortable: false, text: (r) => r.state ?? '' },
    { field: 'city', header: 'City', width: 60, sortable: false, text: (r) => r.city ?? '' },
    { field: 'birthday', header: 'Birthday', width: 80, sortable: false, text: (r) => (r.birthday ? fmtLocalDate(r.birthday) : '') },
    { field: 'initial', header: 'Initial', width: 55, sortable: false, text: (r) => r.initial ?? '' },
    { field: 'position9', header: 'Position', width: 70, sortable: false, filterable: false, text: () => '' },
    { field: 'jobTitle', header: 'JobTitle', width: 85, sortable: false, text: (r) => r.jobTitle ?? '' },
    { field: 'officePhone', header: 'OfficePhone', width: 90, sortable: false, text: (r) => r.officePhone ?? '' },
    { field: 'mobilePhone', header: 'MobilePhone', width: 90, sortable: false, text: (r) => r.mobilePhone ?? '' },
    { field: 'employmentDate', header: 'EmploymentDate', width: 95, sortable: false, text: (r) => (r.employmentDate ? fmtLocalDate(r.employmentDate) : '') },
    { field: 'password', header: 'Password', width: 75, sortable: false, filterable: false, text: () => '' },
    { field: 'startDate', header: 'StartDate', width: 80, sortable: false, text: (r) => (r.activityStart ? fmtLocalDate(r.activityStart) : '') },
    { field: 'department', header: 'EmpDepartment', width: 75, sortable: false, text: (r) => r.department ?? '' },
    { field: 'empPosition', header: 'EmpPosition', width: 80, sortable: false, text: (r) => r.position ?? '' },
    { field: 'roleName', header: 'RoleName', width: 75, sortable: false, text: (r) => r.role ?? '' },
    { field: 'role', header: 'Role', width: 70, sortable: false, text: (r) => r.role ?? '' },
    { field: 'activitys', header: 'Activitys', width: 70, sortable: false, filterable: false, text: () => '' },
    { field: 'isManager', header: 'IsManager', width: 70, align: 'center', sortable: false, filterable: false, render: (r) => check(r.isManager) },
    { field: 'reportingTo', header: 'ReportingTo', width: 80, sortable: false, text: (r) => r.reportingTo ?? '' },
    { field: 'mailServerName', header: 'MailServerName', width: 80, sortable: false, text: (r) => r.mailServerName ?? '' },
    { field: 'port', header: 'Port', width: 50, align: 'right', sortable: false, text: (r) => (r.port != null ? String(r.port) : '') },
    { field: 'enableSSL', header: 'EnableSSL', width: 70, align: 'center', sortable: false, filterable: false, render: (r) => check(r.enableSSL) },
    { field: 'sampleRegistrationDefault', header: 'SampleRegistrationDefault', width: 70, align: 'center', sortable: false, filterable: false, render: (r) => check(r.sampleRegistrationDefault) },
    { field: 'reportDeliveryDefault', header: 'ReportDeliveryDefault', width: 80, align: 'center', sortable: false, filterable: false, render: (r) => check(r.reportDeliveryDefault) },
    { field: 'invoiceDeliveryDefault', header: 'InvoiceDeliveryDefault', width: 80, align: 'center', sortable: false, filterable: false, render: (r) => check(r.invoiceDeliveryDefault) },
    { field: 'suboutNotificationDefault', header: 'SuboutNotificationDefault', width: 75, align: 'center', sortable: false, filterable: false, render: (r) => check(r.suboutNotificationDefault) },
  ];
  // LIMS Employee link popup: a per-row edit pencil + the DisplayName column.
  const linkCols: AppColumn<DeptEmployee>[] = [
    {
      field: 'edit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) => <button className="pts-icon-btn edit" title="Edit" onClick={(e) => { e.stopPropagation(); setEditId(r.id); }}><i className="ti ti-edit" /></button>,
    },
    { field: 'displayName', header: 'DisplayName', minWidth: 300, text: (r) => r.displayName ?? '', compare: cmp((r) => r.displayName) },
  ];

  useHotkeys([
    { combo: 'alt+l', label: 'Link', group: 'Employees', order: 1, run: openLink },
    { combo: 'alt+k', label: 'Unlink', group: 'Employees', order: 2, disabled: selectedIds.size === 0, run: () => { void doUnlink(); } },
    { combo: 'alt+e', label: 'Edit', group: 'Employees', order: 3, disabled: selectedIds.size !== 1, run: editSelected },
    { combo: 'alt+d', label: 'Delete', group: 'Employees', order: 4, disabled: selectedIds.size === 0, run: () => { void removeSelected(); } },
  ]);

  return (
    <div className="acc-subgrid">
      <div className="acc-sub-toolbar">
        <button className="ca-btn" title={`Link (${formatCombo('alt+l')})`} onClick={openLink}><i className="ti ti-link" /> Link</button>
        <button className="ca-btn" title={`Unlink (${formatCombo('alt+k')})`} onClick={() => void doUnlink()} disabled={selectedIds.size === 0}><i className="ti ti-unlink" /> Unlink</button>
        <button className="ca-btn" title={`Edit (${formatCombo('alt+e')})`} onClick={editSelected} disabled={selectedIds.size !== 1}><i className="ti ti-edit" /> Edit</button>
        <button className="ca-btn" title={`Delete (${formatCombo('alt+d')})`} onClick={() => void removeSelected()} disabled={selectedIds.size === 0}><i className="ti ti-trash" /> Delete</button>
        <ExportMenu triggerClassName="ca-btn" filename="DepartmentEmployees"
          headers={['DisplayName', 'Gender', 'ShowAll', 'FullName', 'State', 'City', 'Birthday', 'Initial', 'Position', 'JobTitle', 'OfficePhone', 'MobilePhone', 'EmploymentDate', 'Password', 'StartDate', 'EmpDepartment', 'EmpPosition', 'RoleName', 'Role', 'Activitys', 'IsManager', 'ReportingTo', 'MailServerName', 'Port', 'EnableSSL', 'SampleRegistrationDefault', 'ReportDeliveryDefault', 'InvoiceDeliveryDefault', 'SuboutNotificationDefault']}
          rows={rows.map((r) => [r.displayName ?? '', GENDER_LABEL(r.gender), r.isLIMSAdministrator ? 'Yes' : 'No', r.fullName ?? '', r.state ?? '', r.city ?? '', r.birthday ? fmtLocalDate(r.birthday) : '', r.initial ?? '', '', r.jobTitle ?? '', r.officePhone ?? '', r.mobilePhone ?? '', r.employmentDate ? fmtLocalDate(r.employmentDate) : '', '', r.activityStart ? fmtLocalDate(r.activityStart) : '', r.department ?? '', r.position ?? '', r.role ?? '', r.role ?? '', '', r.isManager ? 'Yes' : 'No', r.reportingTo ?? '', r.mailServerName ?? '', r.port != null ? String(r.port) : '', r.enableSSL ? 'Yes' : 'No', r.sampleRegistrationDefault ? 'Yes' : 'No', r.reportDeliveryDefault ? 'Yes' : 'No', r.invoiceDeliveryDefault ? 'Yes' : 'No', r.suboutNotificationDefault ? 'Yes' : 'No'])} />
      </div>

      <AppDataGrid<DeptEmployee>
        rows={rows} columns={cols} getRowId={(r) => r.id}
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={editId}
        initialSort={{ key: 'displayName', dir: 'asc' }} loading={!staging && isFetching} emptyText="No data to display"
        defaultPageSize={20}
      />

      {linkOpen && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); setLinkOpen(false); }}>
          <div className="acc-dialog acc-dialog-pick acc-dialog--accent" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>Employee</span><i className="ti ti-x acc-dialog-x" onClick={() => setLinkOpen(false)} /></div>
            <div className="acc-dialog-body">
              <div className="acc-pick-search"><div className="acc-search"><i className="ti ti-search" />
                <input placeholder="Search…" autoFocus value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} />
                {linkSearch && <i className="ti ti-x acc-search-x" onClick={() => setLinkSearch('')} />}
              </div></div>
              <AppDataGrid<DeptEmployee>
                rows={linkRows} columns={linkCols} getRowId={(r) => r.id}
                selectable selectedIds={linkSel} onSelectionChange={setLinkSel}
                initialSort={{ key: 'displayName', dir: 'asc' }} emptyText="No employees available to link."
                defaultPageSize={20}
              />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" disabled={busy || linkSel.size === 0} onClick={() => void doLink()}>OK</button>
              <button className="ca-btn" disabled={busy} onClick={() => setLinkOpen(false)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Full Employee detail form, opened from the Edit pencil / Edit button. */}
      {editId && <EmployeeFormDialog employeeId={editId} onClose={() => setEditId(null)} onSaved={() => setEditId(null)} />}
    </div>
  );
}
